import { Button, Spinner } from 'flowbite-react';
import moment from 'moment';
import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
	ApplicationRoundStatus,
	ApplicationStatus,
	BaseAPIResponse,
	CreateApplicationInput,
	UIApplicationCardState,
	UIExtendedApplication,
} from 'types';
import { UseMutationResult, useMutation, useQueryClient } from 'react-query';
import ThreeDotMenu from './ThreeDotMenu';
import { createApplicationV2 } from 'src/apis/application';
import { useToast } from 'src/context/ToastProvider';
import { FaChevronRight } from 'react-icons/fa6';
import { useThemeContext } from 'src/context/ThemeProvider';
import { HiClock } from 'react-icons/hi';
interface Props {
	application: UIExtendedApplication;
	applicationCardState: UIApplicationCardState;
	setIsNotInterestedModalOpen: (arg: boolean) => void;
	setIsCancelInterviewModalOpen: (arg: boolean) => void;
	setIsLinkedInResumeModalOpen: (arg: boolean) => void;
	createApplicationLoading: boolean;
	createApplicationMutation: UseMutationResult<BaseAPIResponse, Error, CreateApplicationInput>;
	setCurrentlySelectedApplication: (arg: UIExtendedApplication) => void;
	optOutOfApplicationProcessLoading: boolean;
}
const ApplicationCardFooter = ({
	setIsNotInterestedModalOpen,
	setIsCancelInterviewModalOpen,

	setIsLinkedInResumeModalOpen,
	application,
	applicationCardState,
	createApplicationLoading,
	setCurrentlySelectedApplication,
	optOutOfApplicationProcessLoading,
}: Props) => {
	const toast = useToast();
	const { themeMode } = useThemeContext();
	const queryClient = useQueryClient();
	const jobseekerExpiryDate = application.ongoingRound?.jobSeekerAssignmentExpiry || application.ongoingRound?.expiry;
	const jobSeekerAssignmentDeadlineText = jobseekerExpiryDate
		? moment(jobseekerExpiryDate).format('DD MMM, hh:mm A')
		: '';
	const interviewStartDateText = moment(application?.ongoingRound?.interviewStartTime).format('DD MMM, hh:mm A');
	const [searchParams, setSearchParams] = useSearchParams();
	const [showThreeDotMenu, setShowThreeDotMenu] = React.useState(false);
	const navigate = useNavigate();
	useEffect(() => {
		setShowThreeDotMenu(
			[
				UIApplicationCardState.INTERVIEW_ROUND_IN_PROGRESS_CARD,
				UIApplicationCardState.ASSIGNMENT_ROUND_IN_PROGRESS_CARD,
				UIApplicationCardState.OFFERED_CARD,
				// UIApplicationCardState.SHORTLISTED_CARD,
				// UIApplicationCardState.WAITING_FOR_RECRUITER_ACTION_CARD,
				UIApplicationCardState.QUALIFIED_ALL_ROUNDS_CARD,
			].includes(applicationCardState),
		);
	}, [applicationCardState]);

	const createApplicationMutation = useMutation(['createApplication'], createApplicationV2, {
		onSuccess() {
			toast({
				title: 'Application created successfully',
				type: 'success',
			});
			queryClient.refetchQueries(['getUserProfile']);
			queryClient.refetchQueries(['getAllApplications']);
		},
		onError(error: Error) {
			toast({
				title: `Error: ${error.message}`,
				type: 'error',
			});
		},
	});

	const getCTASection = () => {
		if (applicationCardState === UIApplicationCardState.MATCHED_CARD) {
			return (
				<div className="flex gap-[12px]">
					<Button
						disabled={createApplicationMutation?.isLoading || optOutOfApplicationProcessLoading || application.expiryDate && moment(application.expiryDate).isBefore(moment())}
						onClick={e => {
							e.stopPropagation();

							const userLinkedInProfile = application.user?.profile.linkedInProfile;
							console.log('userLinkedInProfile:', userLinkedInProfile);
							const userResumeLink = application.user?.profile.resumeLink;
							console.log('userResumeLink:', userResumeLink);

							if (userLinkedInProfile && userResumeLink) {
								createApplicationMutation.mutate({
									createInfo: {
										position: application.position?._id.toString() || '',
										user: application.user?._id || '',
									},
								});
							} else {
								setCurrentlySelectedApplication(application);
								setIsLinkedInResumeModalOpen(true);
							}
						}}
						color="purplePlaced"
						size={'md'}
					>
						{createApplicationMutation?.isLoading ? (
							<div className="absolute flex justify-center inset-0 rounded-[inherit] items-center w-full h-full backdrop-blur-md">
								<Spinner className="float-right" />
							</div>
						) : null}
						Apply
					</Button>
					<Button
						disabled={createApplicationMutation?.isLoading || optOutOfApplicationProcessLoading}
						onClick={e => {
							e.stopPropagation();
							setIsNotInterestedModalOpen(true);
							setCurrentlySelectedApplication(application);
						}}
						size={'md'}
						color={themeMode}
					>
						{optOutOfApplicationProcessLoading ? <Spinner /> : 'Not Interested'}
					</Button>
				</div>
			);
		} else if (applicationCardState === UIApplicationCardState.APPLIED_CARD) {
			return (
				<div className="flex gap-[12px]">
					<Button color="purplePlaced" size={'md'} disabled>
						Applied
					</Button>
				</div>
			);
		} else if (
			[UIApplicationCardState.QUALIFIED_ALL_ROUNDS_CARD].includes(applicationCardState) ||
			[UIApplicationCardState.PENDING_FOR_OFFER_LETTER_APPROVAL_CARD].includes(applicationCardState)
		) {
			return (
				<div>
					<p className="text-[#fff] font-inter text-[12px] font-[400] leading-[16px] mr-[8px]">
						Please await instructions for the next steps.
					</p>
				</div>
			);
		} else if ([UIApplicationCardState.INTERVIEW_ROUND_IN_PROGRESS_CARD].includes(applicationCardState)) {
			return (
				<div className="flex gap-[16px] items-center">
					<div className="flex gap-[10px] items-center">
						{application.ongoingRound?.status === ApplicationRoundStatus.RequestedForReschedule ? (
							<p className="dark:text-[#fff] font-inter text-[12px] font-[400] leading-[16px] mr-[8px]">
								Requested reschedule of interview. Await instructions for the next steps.
							</p>
						) : (
							<>
								<Button
									disabled={!application.ongoingRound?.interviewOrAssignmentLink}
									onClick={e => {
										e.stopPropagation();
										window.open(application.ongoingRound?.interviewOrAssignmentLink || '', '_blank');
									}}
									color="purplePlaced"
									size={'md'}
								// disabled={!isInterviewStarted}
								>
									Join
								</Button>
								<div className="gap-[4px]">
									<p className="dark:text-[#C7C9D9] font-inter text-[12px] font-[400] leading-[16px]">Interview on</p>
									<div className="flex items-center mt-[4px] gap-[4px]">
										<HiClock className="w-[16px] h-[16px] dark:fill-[#FDED72] fill-[#E57A00]" />
										<p className="text-[#E57A00] dark:text-[#FDED72] font-inter text-[12px] font-[400] leading-[16px]">
											{interviewStartDateText}
										</p>
									</div>
								</div>
							</>
						)}
					</div>
				</div>
			);
		} else if (
			[
				UIApplicationCardState.ASSIGNMENT_ROUND_IN_PROGRESS_CARD,
				UIApplicationCardState.PENDING_FOR_ASSIGNMENT_SUBMISSION_APPROVAL_CARD,
			].includes(applicationCardState)
		) {
			return (
				<div className="flex gap-[16px] items-center">
					<div className="flex gap-[10px] items-center">
						<Button
							color="purplePlaced"
							size={'md'}
							onClick={e => {
								e.stopPropagation();
								const newSearchParams = new URLSearchParams(searchParams);
								if (application?.ongoingRound?.type === 'OnlineTest') {
									newSearchParams.set('viewOnlineTest', application._id || '');
								} else {
									newSearchParams.set('viewAssignment', application._id || '');
								}
								setSearchParams(newSearchParams);
							}}
						>
							{application?.ongoingRound?.type === 'OnlineTest' ? 'View Online Test' : 'View Assignment'}
						</Button>
						{jobSeekerAssignmentDeadlineText && (
							<div className="gap-[4px]">
								<p className="dark:text-[#C7C9D9] font-inter text-[12px] font-[400] leading-[16px]">Expires at</p>
								<div className="flex items-center mt-[4px] gap-[4px]">
									<HiClock className="w-[16px] h-[16px] dark:fill-[#FDED72] fill-[#E57A00]" />
									<p className="text-[#E57A00] dark:text-[#FDED72] font-inter text-[12px] font-[400] leading-[16px]">
										{jobSeekerAssignmentDeadlineText}
									</p>
								</div>
							</div>
						)}
					</div>
				</div>
			);
		} else if (
			applicationCardState === UIApplicationCardState.OFFERED_CARD ||
			applicationCardState === UIApplicationCardState.PLACED_CARD ||
			((applicationCardState === UIApplicationCardState.PENDING_FOR_OPT_OUT_APPROVAL_CARD ||
				applicationCardState === UIApplicationCardState.CHANGED_OFFER_TERMS_CARD) &&
				application.history?.some(history => history.newStatus === ApplicationStatus.OFFER_ROLLED_OUT))
		) {
			return (
				<div className="flex gap-[12px]">
					<Button
						onClick={e => {
							e.stopPropagation();
							const newSearchParams = new URLSearchParams(searchParams);
							newSearchParams.set('viewOffer', application._id || ''); // Set new or update existing param
							setSearchParams(newSearchParams);
						}}
						color="purplePlaced"
						size={'md'}
					>
						View Offer
					</Button>
				</div>
			);
		} else if (
			applicationCardState === UIApplicationCardState.CHANGED_OFFER_TERMS_CARD &&
			!application.history?.some(history => history.newStatus === ApplicationStatus.OFFER_ROLLED_OUT)
		) {
			return (
				<div className="flex gap-[12px]">
					<Button
						onClick={e => {
							e.stopPropagation();
							const newSearchParams = new URLSearchParams(searchParams);
							newSearchParams.set('viewUpdatedTerms', application._id || ''); // Set new or update existing param
							setSearchParams(newSearchParams);
						}}
						color="purplePlaced"
						size={'md'}
					>
						View Updated Terms
					</Button>
				</div>
			);
		}
	};

	return (
		<>
			<div className="cursor-pointer items-center dark:bg-[#111E2C] bg-[#F8FAFC] py-3 px-3 rounded-bl-[12px] rounded-br-[12px] flex justify-between">
				<div>{getCTASection()}</div>

				<div className="flex items-center gap-2">
					<Button
						color="purplePlaced"
						size="md"
						onClick={e => {
							e.stopPropagation();
							const companyId = typeof application.position?.company === 'object'
								? application.position.company?._id
								: application.position?.company;
							const params = new URLSearchParams({
								questionType: 'company',
								modal: 'search',
								batchId: '0',
								...(companyId && { companyId: String(companyId) }),
								...(application.position?._id && { positionId: String(application.position._id) }),
							});
							// applicationId is optional - only pass the application document ID for tracking
							if (application._id) {
								params.set('applicationId', application._id);
							}
							navigate(`/support?${params.toString()}`);
						}}
					>
						Create Ticket
					</Button>
					{showThreeDotMenu ? (
						<ThreeDotMenu application={application} applicationCardState={applicationCardState} />
					) : (
						<div className="pr-2">
							<FaChevronRight className="h-full w-full min-h-[24px] min-w-[24px] max-h-[24px] max-w-[24px] text-[#7777E7] dark:text-[#CCDDFF]" />
						</div>
					)}
				</div>
			</div>
		</>
	);
};

export default ApplicationCardFooter;
